import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : undefined;

// Read timeout from env (ms)
const TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 30000;

// Create axios instance
const api = axios.create({
  baseURL: 'https://gangotri.parashitech.in/backend', // API_BASE, // Proxy will handle this
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle auth
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      try {
        if (typeof window !== 'undefined' && window.location?.pathname !== '/login') {
          window.location.href = '/login';
        }
      } catch (e) {
        // ignore
      }
    }

    // Normalize network/abort errors so UI can display friendlier messages.
    if (!error.response) {
      const msg = (error.message || '').toString().toLowerCase();
      const isTimeout = error.code === 'ECONNABORTED' || msg.includes('timeout');
      const isAborted = msg.includes('ns_binding_aborted') || msg.includes('aborted');
      const isNetwork = msg.includes('network error') || msg.includes('network');

      error.isNetworkError = isNetwork || isAborted;
      if (isTimeout) error.isTimeout = true;
      if (isAborted) error.isAborted = true;
      // attach a normalized message
      if (isTimeout) {
        error.normalizedMessage = 'Request timed out.';
      } else if (isAborted) {
        error.normalizedMessage = 'Request aborted by the browser.';
      } else if (isNetwork) {
        error.normalizedMessage = 'Network error. Check your connection.';
      } else {
        error.normalizedMessage = 'Unknown network error.';
      }
    }

    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }),
  getCurrentUser: () =>
    api.get('/auth/me'),
};

// Dashboard APIs
export const dashboardAPI = {
  getCounts: () => api.get('/dashboard/counts'),
};

// Master Tables APIs
export const masterAPI = {
  // Machine Types
  getMachineTypes: (page = 1, pageSize = 10, search = '') =>
    api.get('/master/machine-types', { params: { page, page_size: pageSize, search: search || undefined } }),
  getAllMachineTypes: () =>
    api.get('/master/machine-types/all'),
  createMachineType: (name) =>
    api.post('/master/machine-types', { name }),
  updateMachineType: (id, name) =>
    api.put(`/master/machine-types/${id}`, { name }),
  deleteMachineType: (id) =>
    api.delete(`/master/machine-types/${id}`),

  // Manufacturers
  getManufacturers: (page = 1, pageSize = 10, search = '') =>
    api.get('/master/manufacturers', { params: { page, page_size: pageSize, search: search || undefined } }),
  getAllManufacturers: () =>
    api.get('/master/manufacturers/all'),
  createManufacturer: (name) =>
    api.post('/master/manufacturers', { name }),
  updateManufacturer: (id, name) =>
    api.put(`/master/manufacturers/${id}`, { name }),
  deleteManufacturer: (id) =>
    api.delete(`/master/manufacturers/${id}`),

  // Models
  getModels: (page = 1, pageSize = 10, search = '') =>
    api.get('/master/models', { params: { page, page_size: pageSize, search: search || undefined } }),
  getAllModels: () =>
    api.get('/master/models/all'),
  createModel: (name) =>
    api.post('/master/models', { name }),
  updateModel: (id, name) =>
    api.put(`/master/models/${id}`, { name }),
  deleteModel: (id) =>
    api.delete(`/master/models/${id}`),

  // Part Names
  getPartNames: (page = 1, pageSize = 10, search = '') =>
    api.get('/master/part-names', { params: { page, page_size: pageSize, search: search || undefined } }),
  getAllPartNames: () =>
    api.get('/master/part-names/all'),
  createPartName: (name) =>
    api.post('/master/part-names', { name }),
  updatePartName: (id, name) =>
    api.put(`/master/part-names/${id}`, { name }),
  deletePartName: (id) =>
    api.delete(`/master/part-names/${id}`),
};

// Products APIs
export const productsAPI = {
  getProducts: (page = 1, pageSize = 10, search = '', filters = {}) =>
    api.get('/products', {
      params: {
        page,
        page_size: pageSize,
        search: search || undefined,
        ...filters
      }
    }),
  getProduct: (id) =>
    api.get(`/products/${id}`),
  createProduct: (data) =>
    api.post('/products', data),
  updateProduct: (id, data) =>
    api.put(`/products/${id}`, data),
  deleteProduct: (id) =>
    api.delete(`/products/${id}`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/products', formData, {
      params: {
        id,
        action: 'upload-image',
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Prasco Products APIs
export const prascoProductsAPI = {
  getProducts: (page = 1, pageSize = 10, search = '', filters = {}) =>
    api.get('/prasco-products', {
      params: {
        page,
        page_size: pageSize,
        search: search || undefined,
        ...filters
      }
    }),
  getProduct: (id) =>
    api.get(`/prasco-products/${id}`),
  createProduct: (data) =>
    api.post('/prasco-products', data),
  updateProduct: (id, data) =>
    api.put(`/prasco-products/${id}`, data),
  deleteProduct: (id) =>
    api.delete(`/prasco-products/${id}`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/prasco-products', formData, {
      params: {
        id,
        action: 'upload-image',
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Inquiries APIs
export const inquiriesAPI = {
  // Product Inquiries
  getProductInquiries: (page = 1, pageSize = 10, isRead = null) =>
    api.get('/inquiries/product', {
      params: {
        page,
        page_size: pageSize,
        is_read: isRead
      }
    }),
  markProductInquiryRead: (id) =>
    api.put(`/inquiries/product/${id}/mark-read`),
  markProductInquiryUnread: (id) =>
    api.put(`/inquiries/product/${id}/mark-unread`),

  // Prasco Inquiries
  getPrascoInquiries: (page = 1, pageSize = 10, isRead = null) =>
    api.get('/inquiries/prasco', {
      params: {
        page,
        page_size: pageSize,
        is_read: isRead
      }
    }),
  markPrascoInquiryRead: (id) =>
    api.put(`/inquiries/prasco/${id}/mark-read`),
  markPrascoInquiryUnread: (id) =>
    api.put(`/inquiries/prasco/${id}/mark-unread`),
};

// Leads APIs
export const leadsAPI = {
  getLeads: (page = 1, pageSize = 10, isRead = null) =>
    api.get('/leads', {
      params: {
        page,
        page_size: pageSize,
        is_read: isRead
      }
    }),
  markLeadRead: (id) =>
    api.put('/leads', null, {
      params: {
        id,
        action: 'mark-read',
      },
    }),

  markLeadUnread: (id) =>
    api.put('/leads', null, {
      params: {
        id,
        action: 'mark-unread',
      },
    }),
};

export default api;
